// Subscription access control logic

/**
 * Map Firestore planId to component subscription type
 * Firestore uses: "monthly_global_markets", "monthly_all_dashboards"
 * Component uses: "monthly_global", "monthly_all"
 */
export function mapPlanIdToSubscriptionType(planId) {
  if (!planId) return 'expired';
  
  const planIdMap = {
    'free_trial': 'free_trial',
    'daily': 'daily',
    'monthly_global_markets': 'monthly_global',
    'monthly_all_dashboards': 'monthly_all',
    // Also handle component types (in case already mapped)
    'monthly_global': 'monthly_global',
    'monthly_all': 'monthly_all',
    'expired': 'expired',
    'cancelled': 'cancelled'
  };
  
  return planIdMap[planId] || 'expired';
}

/**
 * Map component subscription type to Firestore planId
 */
export function mapSubscriptionTypeToPlanId(subscriptionType) {
  if (!subscriptionType) return 'expired';
  
  const typeMap = {
    'free_trial': 'free_trial',
    'daily': 'daily',
    'monthly_global': 'monthly_global_markets',
    'monthly_all': 'monthly_all_dashboards',
    'expired': 'expired',
    'cancelled': 'cancelled'
  };
  
  return typeMap[subscriptionType] || 'expired';
}

const DASHBOARD_ACCESS = {
  free_trial: ['global_markets'],
  monthly_global: ['global_markets'],
  monthly_all: ['global_markets', 'global_technology', 'hybrid'],
  expired: [],
  cancelled: [], // Access until cancellation date
};

/**
 * Check if user has access to a specific dashboard type
 * @param {string|object} subscriptionTypeOrObj - User's subscription type (string) or subscription object
 * @param {string} dashboardType - Dashboard type to check ('global_markets', 'global_technology', 'hybrid')
 * @returns {boolean}
 */
export function hasDashboardAccess(subscriptionTypeOrObj, dashboardType) {
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:59',message:'hasDashboardAccess entry',data:{subscriptionTypeOrObj,dashboardType,isObject:typeof subscriptionTypeOrObj==='object'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
  // #endregion
  if (!subscriptionTypeOrObj || !dashboardType) return false;

  // Handle both object and string inputs for backward compatibility
  let subscriptionType;
  let subscriptionObj = null;

  if (typeof subscriptionTypeOrObj === 'object') {
    subscriptionObj = subscriptionTypeOrObj;
    subscriptionType = subscriptionObj.type || 'expired';
    
    const status = subscriptionObj.status || 'expired';
    
    // Daily plan removed; keep check for backward compat with existing daily subscribers
    if (subscriptionType !== 'daily') {
      // For non-daily subscriptions, check expiration status FIRST before checking type access
      // If status is expired, deny access immediately regardless of subscription type
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:76',message:'Checking status before early return (non-daily)',data:{status,subscriptionType,dashboardType,subscriptionObj},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      if (status === 'expired') {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:82',message:'EARLY RETURN: status is expired (non-daily)',data:{status,subscriptionType,dashboardType,subscriptionObj},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        console.log('[hasDashboardAccess] Access denied: status is expired', {
          status,
          subscriptionType,
          dashboardType,
          subscriptionObj
        });
        return false;
      }
    } else {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:92',message:'Skipping status check for daily subscription',data:{status,subscriptionType,dashboardType,subscriptionObj,willCheckPeriod:true},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
    }
    
    // For free trials only, enforce local expiration checks.
    if (subscriptionType === 'free_trial') {
      const now = new Date();
      const trialExpiresAt = subscriptionObj.trialEndsAt || subscriptionObj.currentPeriodEnd || subscriptionObj.expiresAt;
      if (trialExpiresAt) {
        const expirationDate = new Date(trialExpiresAt);
        if (expirationDate < now) {
          console.log('[hasDashboardAccess] Access denied: free trial period expired', {
            trialExpiresAt,
            expirationDate: expirationDate.toISOString(),
            now: now.toISOString(),
            dashboardType
          });
          return false;
        }
      }
    }
  } else {
    subscriptionType = subscriptionTypeOrObj;
    // If subscription type is 'expired', deny access immediately
    if (subscriptionType === 'expired') {
      return false;
    }
  }

  // Check if subscription type has access to this dashboard
  // If subscription type is 'expired', it won't have access to any dashboard
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:118',message:'Checking subscription type access',data:{subscriptionType,dashboardType,isExpiredType:subscriptionType==='expired'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
  // #endregion
  if (subscriptionType === 'expired') {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:123',message:'EARLY RETURN: subscription type is expired',data:{subscriptionType,dashboardType},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
    // #endregion
    console.log('[hasDashboardAccess] Access denied: subscription type is expired', {
      subscriptionType,
      dashboardType
    });
    return false;
  }

  const hasTypeAccess = DASHBOARD_ACCESS[subscriptionType]?.includes(dashboardType) || false;
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:128',message:'Type access check result',data:{subscriptionType,dashboardType,hasTypeAccess,availableDashboards:DASHBOARD_ACCESS[subscriptionType]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
  // #endregion
  if (!hasTypeAccess) {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:134',message:'EARLY RETURN: subscription type does not have access',data:{subscriptionType,dashboardType,availableDashboards:DASHBOARD_ACCESS[subscriptionType]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
    // #endregion
    console.log('[hasDashboardAccess] Access denied: subscription type does not have access', {
      subscriptionType,
      dashboardType,
      availableDashboards: DASHBOARD_ACCESS[subscriptionType]
    });
    return false;
  }

  // If we have a subscription object, perform additional validation
  if (subscriptionObj) {
    const status = subscriptionObj.status || 'expired';
    // Additional checks for specific subscription types
    
    // For free trials, check if sessions are remaining
    if (subscriptionType === 'free_trial') {
      // If sessionsRemaining is defined and is 0 or less, no access
      if (subscriptionObj.sessionsRemaining !== undefined && subscriptionObj.sessionsRemaining <= 0) {
        console.log('[hasDashboardAccess] Access denied: free trial sessions exhausted', {
          sessionsRemaining: subscriptionObj.sessionsRemaining,
          subscriptionType,
          dashboardType
        });
        return false;
      }
    }

    // For cancelled subscriptions, access is allowed until period end (already checked above)
    // If we got here, period hasn't expired yet, so allow access
    if (status === 'canceled' || status === 'cancelled' || subscriptionObj.cancelAtPeriodEnd) {
      return true;
    }
  }

  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/7f56e3de-d540-4738-9107-ff99e5fcd694',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'accountHelpers.js:187',message:'Access granted',data:{subscriptionType,dashboardType,hasSubscriptionObj:!!subscriptionObj},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
  // #endregion
  console.log('[hasDashboardAccess] Access granted', {
    subscriptionType,
    dashboardType,
    hasSubscriptionObj: !!subscriptionObj
  });
  return true;
}

/**
 * Get display name for subscription type
 * @param {string} subscriptionType - Subscription type
 * @returns {string}
 */
export function getSubscriptionDisplayName(subscriptionType) {
  const names = {
    free_trial: 'Free Trial',
    daily: 'Daily Subscription',
    monthly_global: 'Monthly Global Markets',
    monthly_all: 'Monthly All Dashboards',
    expired: 'No Active Subscription',
    cancelled: 'Cancelled Subscription'
  };
  return names[subscriptionType] || 'Unknown';
}

/**
 * Get all accessible dashboards for a subscription type
 * @param {string} subscriptionType - Subscription type
 * @returns {string[]} Array of accessible dashboard types
 */
export function getAccessibleDashboards(subscriptionType) {
  return DASHBOARD_ACCESS[subscriptionType] || [];
}

/**
 * Check if subscription can be upgraded
 * @param {string} subscriptionType - Current subscription type
 * @returns {boolean}
 */
export function canUpgrade(subscriptionType) {
  const upgradeable = ['free_trial', 'monthly_global', 'expired'];
  return upgradeable.includes(subscriptionType);
}

/**
 * Check if subscription can be downgraded
 * @param {string} subscriptionType - Current subscription type
 * @returns {boolean}
 */
export function canDowngrade(subscriptionType) {
  const downgradeable = ['monthly_all'];
  return downgradeable.includes(subscriptionType);
}

/**
 * Check if subscription can be cancelled
 * @param {string} subscriptionType - Current subscription type
 * @returns {boolean}
 */
export function canCancel(subscriptionType) {
  const cancellable = ['monthly_global', 'monthly_all'];
  return cancellable.includes(subscriptionType);
}

/**
 * Get subscription tier level for comparison
 * @param {string} subscriptionType - Subscription type
 * @returns {number} Tier level (0 = none, 1 = free trial, 2 = daily, 3 = monthly global, 4 = monthly all)
 */
export function getSubscriptionTier(subscriptionType) {
  const tiers = {
    expired: 0,
    cancelled: 0,
    free_trial: 1,
    monthly_global: 2,
    monthly_all: 3
  };
  return tiers[subscriptionType] || 0;
}









